/**
 * Pilot Qualifications Panel
 * Manages type ratings and training records for a pilot.
 * Opened from the admin user table.
 */

import { useState, useEffect } from 'react';
import { api } from '@/services/api';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface TypeRating {
  id: number;
  aircraftType: string;
  ratingDate: string;
  expiryDate?: string;
  notes?: string;
}

interface TrainingRecord {
  id: number;
  trainingType: string;
  aircraftType?: string;
  completedDate: string;
  expiryDate?: string;
  examinerName?: string;
  result: string;
  notes?: string;
}

interface ComplianceSummary {
  licence: { status: string; number?: string; type?: string };
  medical: { status: string; category?: string; expiryDate?: string; daysRemaining?: number };
  training: { ppcStatus: string; lastPpc?: string; ppcExpiry?: string };
}

const TRAINING_TYPES = [
  'PPC', 'IPC', 'RECURRENT', 'LINE_CHECK', 'OCC',
  'INITIAL', 'UPGRADE', 'DIFFERENCES', 'GROUND_SCHOOL', 'OTHER',
];

interface Props {
  pilotId: number;
  pilotName: string;
  onClose: () => void;
}

export default function PilotQualificationsPanel({ pilotId, pilotName, onClose }: Props) {
  const [typeRatings, setTypeRatings] = useState<TypeRating[]>([]);
  const [training, setTraining] = useState<TrainingRecord[]>([]);
  const [summary, setSummary] = useState<ComplianceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add forms
  const [showAddRating, setShowAddRating] = useState(false);
  const [showAddTraining, setShowAddTraining] = useState(false);
  const [ratingForm, setRatingForm] = useState({ aircraftType: '', ratingDate: '', expiryDate: '', notes: '' });
  const [trainingForm, setTrainingForm] = useState({ trainingType: 'PPC', aircraftType: '', completedDate: '', examinerName: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [pilotId]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [ratingsRes, trainingRes, summaryRes] = await Promise.all([
        api.get<{ data: TypeRating[] }>(`/users/${pilotId}/type-ratings`),
        api.get<{ data: TrainingRecord[] }>(`/users/${pilotId}/training`),
        api.get<{ data: ComplianceSummary }>(`/users/${pilotId}/compliance-summary`),
      ]);
      setTypeRatings(ratingsRes.data);
      setTraining(trainingRes.data);
      setSummary(summaryRes.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load qualifications');
    } finally {
      setLoading(false);
    }
  };

  const addTypeRating = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/users/${pilotId}/type-ratings`, {
        aircraftType: ratingForm.aircraftType,
        ratingDate: ratingForm.ratingDate,
        expiryDate: ratingForm.expiryDate || undefined,
        notes: ratingForm.notes || undefined,
      });
      setRatingForm({ aircraftType: '', ratingDate: '', expiryDate: '', notes: '' });
      setShowAddRating(false);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to add type rating');
    } finally {
      setSaving(false);
    }
  };

  const deleteTypeRating = async (id: number) => {
    try {
      await api.delete(`/users/${pilotId}/type-ratings/${id}`);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete type rating');
    }
  };

  const addTrainingRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/users/${pilotId}/training`, {
        trainingType: trainingForm.trainingType,
        aircraftType: trainingForm.aircraftType || undefined,
        completedDate: trainingForm.completedDate,
        examinerName: trainingForm.examinerName || undefined,
        notes: trainingForm.notes || undefined,
      });
      setTrainingForm({ trainingType: 'PPC', aircraftType: '', completedDate: '', examinerName: '', notes: '' });
      setShowAddTraining(false);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to add training record');
    } finally {
      setSaving(false);
    }
  };

  const deleteTrainingRecord = async (id: number) => {
    try {
      await api.delete(`/users/${pilotId}/training/${id}`);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete training record');
    }
  };

  const formatDate = (d?: string) => {
    if (!d) return '-';
    const [y, m, day] = d.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString();
  };

  const expiryColor = (d?: string) => {
    if (!d) return '';
    const days = Math.ceil((new Date(d).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'text-red-600 dark:text-red-400';
    if (days < 30) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      valid: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400',
      warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-400',
      critical: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-400',
      expired: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400',
      missing: 'bg-muted text-muted-foreground',
      recorded: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400',
    };
    return <span className={`px-2 py-0.5 text-xs rounded-full ${colors[status] || colors.missing}`}>{status}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background z-10">
          <h2 className="text-lg font-semibold">Qualifications — {pilotName}</h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded"><XMarkIcon className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-6">
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 rounded text-sm">{error}</div>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Compliance Summary */}
              {summary && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-card border border-border rounded-lg p-3">
                    <div className="text-xs text-muted-foreground mb-1">Licence</div>
                    <div className="font-medium text-sm">{summary.licence.type || '-'} {summary.licence.number || ''}</div>
                    {statusBadge(summary.licence.status)}
                  </div>
                  <div className="bg-card border border-border rounded-lg p-3">
                    <div className="text-xs text-muted-foreground mb-1">Medical</div>
                    <div className="font-medium text-sm">
                      {summary.medical.category ? `Cat ${summary.medical.category}` : '-'}
                      {summary.medical.daysRemaining != null && (
                        <span className={`ml-1 ${expiryColor(summary.medical.expiryDate ?? undefined)}`}>
                          ({summary.medical.daysRemaining}d)
                        </span>
                      )}
                    </div>
                    {statusBadge(summary.medical.status)}
                  </div>
                  <div className="bg-card border border-border rounded-lg p-3">
                    <div className="text-xs text-muted-foreground mb-1">PPC</div>
                    <div className="font-medium text-sm">
                      {summary.training.ppcExpiry ? formatDate(summary.training.ppcExpiry) : '-'}
                    </div>
                    {statusBadge(summary.training.ppcStatus)}
                  </div>
                </div>
              )}

              {/* Type Ratings */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-foreground">Type Ratings</h3>
                  <button
                    onClick={() => setShowAddRating(!showAddRating)}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                  >
                    <PlusIcon className="w-3.5 h-3.5" /> Add
                  </button>
                </div>

                {showAddRating && (
                  <form onSubmit={addTypeRating} className="bg-muted/50 rounded-lg p-3 mb-3 space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        placeholder="Aircraft Type (e.g. CE-560XL)"
                        value={ratingForm.aircraftType}
                        onChange={(e) => setRatingForm({ ...ratingForm, aircraftType: e.target.value })}
                        required
                        className="px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground"
                      />
                      <input
                        type="date"
                        value={ratingForm.ratingDate}
                        onChange={(e) => setRatingForm({ ...ratingForm, ratingDate: e.target.value })}
                        required
                        className="px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground"
                      />
                      <input
                        type="date"
                        placeholder="Expiry (optional)"
                        value={ratingForm.expiryDate}
                        onChange={(e) => setRatingForm({ ...ratingForm, expiryDate: e.target.value })}
                        className="px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setShowAddRating(false)} className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                      <button type="submit" disabled={saving} className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </form>
                )}

                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Aircraft Type</th>
                        <th className="px-3 py-2 text-left font-medium">Rating Date</th>
                        <th className="px-3 py-2 text-left font-medium">Expiry</th>
                        <th className="px-3 py-2 text-right font-medium w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {typeRatings.length === 0 ? (
                        <tr><td colSpan={4} className="px-3 py-4 text-center text-muted-foreground text-xs">No type ratings recorded</td></tr>
                      ) : typeRatings.map((r) => (
                        <tr key={r.id}>
                          <td className="px-3 py-2 font-mono font-medium">{r.aircraftType}</td>
                          <td className="px-3 py-2">{formatDate(r.ratingDate)}</td>
                          <td className={`px-3 py-2 ${expiryColor(r.expiryDate)}`}>{formatDate(r.expiryDate)}</td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => deleteTypeRating(r.id)} className="text-muted-foreground hover:text-red-500">
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Training Records */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-foreground">Training Records</h3>
                  <button
                    onClick={() => setShowAddTraining(!showAddTraining)}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                  >
                    <PlusIcon className="w-3.5 h-3.5" /> Add
                  </button>
                </div>

                {showAddTraining && (
                  <form onSubmit={addTrainingRecord} className="bg-muted/50 rounded-lg p-3 mb-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={trainingForm.trainingType}
                        onChange={(e) => setTrainingForm({ ...trainingForm, trainingType: e.target.value })}
                        className="px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground"
                      >
                        {TRAINING_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                      </select>
                      <input
                        type="date"
                        value={trainingForm.completedDate}
                        onChange={(e) => setTrainingForm({ ...trainingForm, completedDate: e.target.value })}
                        required
                        className="px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        placeholder="Aircraft Type (optional)"
                        value={trainingForm.aircraftType}
                        onChange={(e) => setTrainingForm({ ...trainingForm, aircraftType: e.target.value })}
                        className="px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground"
                      />
                      <input
                        placeholder="Examiner Name"
                        value={trainingForm.examinerName}
                        onChange={(e) => setTrainingForm({ ...trainingForm, examinerName: e.target.value })}
                        className="px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setShowAddTraining(false)} className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                      <button type="submit" disabled={saving} className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </form>
                )}

                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Type</th>
                        <th className="px-3 py-2 text-left font-medium">Aircraft</th>
                        <th className="px-3 py-2 text-left font-medium">Completed</th>
                        <th className="px-3 py-2 text-left font-medium">Expiry</th>
                        <th className="px-3 py-2 text-left font-medium">Result</th>
                        <th className="px-3 py-2 text-right font-medium w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {training.length === 0 ? (
                        <tr><td colSpan={6} className="px-3 py-4 text-center text-muted-foreground text-xs">No training records</td></tr>
                      ) : training.map((t) => (
                        <tr key={t.id}>
                          <td className="px-3 py-2 font-medium">{t.trainingType.replace('_', ' ')}</td>
                          <td className="px-3 py-2 font-mono">{t.aircraftType || '-'}</td>
                          <td className="px-3 py-2">{formatDate(t.completedDate)}</td>
                          <td className={`px-3 py-2 ${expiryColor(t.expiryDate)}`}>{formatDate(t.expiryDate)}</td>
                          <td className="px-3 py-2">
                            <span className={`px-1.5 py-0.5 text-xs rounded ${
                              t.result === 'PASS' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400' :
                              t.result === 'FAIL' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400' :
                              'bg-muted text-muted-foreground'
                            }`}>{t.result}</span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => deleteTrainingRecord(t.id)} className="text-muted-foreground hover:text-red-500">
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
